/**
 * @fileoverview Example API Contract (Contract-First Design)
 * @module types/contracts
 *
 * BEST PRACTICE: Contract-First API Design with Zod
 *
 * This is the POC demonstrating the cleanest, most reusable approach:
 * 1. Define core schemas (entities, payloads)
 * 2. Derive API response schemas using helpers
 * 3. Define API contract with routes
 * 4. Export everything for backend/frontend use
 *
 * PRINCIPLES:
 * - Single source of truth
 * - Zero duplication
 * - Reusable schemas
 * - Type safety everywhere
 */

import { z } from 'zod';
import {
	createContract,
	extractRoutes,
	ApiSuccessResponseSchema,
	ApiPaginatedResponseSchema,
} from './contract.utils';

// ============================================================================
// CORE SCHEMAS - Domain Models
// ============================================================================

/**
 * Create Product Payload Schema
 */
export const CreateProductSchema = z.object({
	name: z.string().min(2).describe('Product name'),
	description: z.string().min(10).describe('Product description'),
	price: z.number().positive().describe('Product price'),
	stock: z.number().int().nonnegative().describe('Stock quantity'),
	category: z.string().min(2).describe('Product category'),
});
export type CreateProductPayload = z.infer<typeof CreateProductSchema>;

/**
 * Product Entity Schema
 *
 * IMPORTANT: Date fields use z.iso.datetime() for ISO 8601 strings in the API contract.
 * The ZodSerializerInterceptor will automatically convert Date objects from DB entities to ISO strings.
 */
export const ProductSchema = CreateProductSchema.extend({
	id: z.number().positive().describe('Unique product ID'),
	createdAt: z.iso.datetime().describe('Creation timestamp (ISO 8601)'),
	updatedAt: z.iso.datetime().describe('Last update timestamp (ISO 8601)'),
});
export type ProductEntity = z.infer<typeof ProductSchema>;

/**
 * Update Product Payload Schema
 */
export const UpdateProductSchema = CreateProductSchema.partial();
export type UpdateProductPayload = z.infer<typeof UpdateProductSchema>;

/**
 * Product ID Parameter Schema
 */
export const ProductIdParamSchema = z.object({
	id: z.string().regex(/^\d+$/, 'ID must be a number').transform(Number),
});

/**
 * List Products Query Schema
 *
 * IMPORTANT: Use z.coerce.number() for query parameters to generate proper OpenAPI spec
 * - z.string().transform(Number) generates type: "string" in OpenAPI (WRONG)
 * - z.coerce.number() generates type: "integer" in OpenAPI (CORRECT)
 */
export const ListProductsQuerySchema = z.object({
	page: z.coerce.number().int().positive().default(1),
	limit: z.coerce.number().int().positive().max(100).default(10),
	category: z.string().optional(),
});
export type ListProductsQuery = z.infer<typeof ListProductsQuerySchema>;

// ============================================================================
// API RESPONSE SCHEMAS
// ============================================================================

/**
 * Single Product API Response
 * { success: true, data: ProductEntity, timestamp: string }
 */
export const ProductApiResponseSchema = ApiSuccessResponseSchema(ProductSchema);
export type ProductApiResponse = z.infer<typeof ProductApiResponseSchema>;

/**
 * Paginated Products API Response
 * { success: true, data: ProductEntity[], meta: PaginationMeta, timestamp: string }
 */
export const PaginatedProductsApiResponseSchema = ApiPaginatedResponseSchema(ProductSchema);
export type PaginatedProductsApiResponse = z.infer<typeof PaginatedProductsApiResponseSchema>;

/**
 * Delete Product API Response
 * { success: true, data: { message: string }, timestamp: string }
 */
const DeleteProductDataSchema = z.object({ message: z.string() });
export const DeleteProductApiResponseSchema = ApiSuccessResponseSchema(DeleteProductDataSchema);
export type DeleteProductApiResponse = z.infer<typeof DeleteProductApiResponseSchema>;

// ============================================================================
// API CONTRACT - Route Definitions
// ============================================================================

/**
 * Example API Contract
 * Defines complete API surface for the Example module
 * Single source of truth for frontend and backend
 */
export const EXAMPLE_API_CONTRACT = createContract('/example/products', {
	/**
	 * POST /example/products - Create product
	 */
	create: {
		method: 'POST' as const,
		path: '',
		body: CreateProductSchema,
		response: ProductApiResponseSchema,
	},

	/**
	 * GET /example/products - List products with pagination
	 */
	list: {
		method: 'GET' as const,
		path: '',
		query: ListProductsQuerySchema,
		response: PaginatedProductsApiResponseSchema,
	},

	/**
	 * GET /example/products/:id - Get single product
	 */
	getOne: {
		method: 'GET' as const,
		path: '/:id',
		params: ProductIdParamSchema,
		response: ProductApiResponseSchema,
	},

	/**
	 * PATCH /example/products/:id - Update product
	 */
	update: {
		method: 'PATCH' as const,
		path: '/:id',
		params: ProductIdParamSchema,
		body: UpdateProductSchema,
		response: ProductApiResponseSchema,
	},

	/**
	 * DELETE /example/products/:id - Delete product
	 */
	delete: {
		method: 'DELETE' as const,
		path: '/:id',
		params: ProductIdParamSchema,
		response: DeleteProductApiResponseSchema,
	},
});

/**
 * Route Constants
 * Use in @Controller() decorators
 */
export const EXAMPLE_ROUTES = extractRoutes(EXAMPLE_API_CONTRACT);
